import { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, ArrowRight, CheckCircle2, AlertCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { LANDING } from '@/constants/testIds';
import { BUSINESS } from '@/data/business';
import { useReveal } from '@/hooks/useReveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = ['Devis pro / B2B', 'Flocage – broderie', 'Visite boutique', 'EPI sur-mesure', 'Autre'];

export const Contact = () => {
    const [ref, visible] = useReveal();
    const [form, setForm] = useState({
        name: '',
        company: '',
        phone: '',
        email: '',
        message: '',
        interests: [INTERESTS[0]],
    });
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(null); // 'success' | 'error'

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const toggleInterest = (item) =>
        setForm((f) => ({
            ...f,
            interests: f.interests.includes(item)
                ? f.interests.filter((x) => x !== item)
                : [...f.interests, item],
        }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!form.name || !form.phone || !form.email || !form.message) {
            toast.error('Merci de compléter votre nom, téléphone, email et message.');
            return;
        }
        setSubmitting(true);
        setStatus(null);
        try {
            const payload = {
                ...form,
                interest: form.interests.join(', '),
            };
            delete payload.interests;
            await axios.post(`${API}/contact`, payload);
            setStatus('success');
            toast.success('Demande envoyée. Nous vous recontactons rapidement.');
            setForm({ name: '', company: '', phone: '', email: '', message: '', interests: [INTERESTS[0]] });
        } catch (err) {
            console.error(err);
            setStatus('error');
            toast.error("Une erreur est survenue. Vous pouvez aussi nous appeler directement.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid={LANDING.contact}
            ref={ref}
            className="relative py-20 sm:py-28 border-t border-white/5 overflow-hidden"
        >
            <div className="absolute -top-32 right-10 h-[420px] w-[420px] blur-orb-yellow opacity-50 pointer-events-none" />

            <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
                <div className={`${visible ? 'in-view' : ''} reveal mb-10 sm:mb-14 max-w-3xl`}>
                    <span className="section-label">08 / Contact</span>
                    <h2 className="mt-4 font-display uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.9] text-white">
                        Passez à la
                        <br />
                        <span className="text-outline-yellow">boutique</span>
                        <span className="text-[#FFD500]">.</span>
                    </h2>
                    <p className="mt-6 text-base sm:text-lg text-white leading-relaxed">
                        Un besoin précis, un devis pro, une commande à flocker ? Appelez-nous, écrivez-nous ou venez nous voir au 37 avenue Charles de Gaulle à Aulnay-sous-Bois.
                    </p>
                </div>

                <div className="grid lg:grid-cols-[1.1fr,1fr] gap-5">
                    {/* Form */}
                    <form
                        data-testid={LANDING.contactForm}
                        onSubmit={onSubmit}
                        className="corner-cut bg-[#0F0F0F] border border-white/10 p-6 sm:p-8 lg:p-10 space-y-5"
                    >
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Nom complet *">
                                <input
                                    data-testid={LANDING.contactName}
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={onChange('name')}
                                    placeholder="Jean Dupont"
                                    className="form-input"
                                />
                            </Field>
                            <Field label="Société">
                                <input
                                    data-testid={LANDING.contactCompany}
                                    type="text"
                                    value={form.company}
                                    onChange={onChange('company')}
                                    placeholder="Optionnel"
                                    className="form-input"
                                />
                            </Field>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Téléphone *">
                                <input
                                    data-testid={LANDING.contactPhone}
                                    type="tel"
                                    required
                                    value={form.phone}
                                    onChange={onChange('phone')}
                                    placeholder="06 00 00 00 00"
                                    className="form-input"
                                />
                            </Field>
                            <Field label="Email *">
                                <input
                                    data-testid={LANDING.contactEmail}
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={onChange('email')}
                                    placeholder="vous@email.fr"
                                    className="form-input"
                                />
                            </Field>
                        </div>

                        <Field label="Sujets (plusieurs choix possibles)">
                            <div className="flex flex-wrap gap-2">
                                {INTERESTS.map((it) => (
                                    <button
                                        type="button"
                                        key={it}
                                        data-testid={`${LANDING.contactInterest}-${it.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                        onClick={() => toggleInterest(it)}
                                        className={`px-3 py-1.5 border text-xs uppercase tracking-wider font-semibold transition-colors corner-cut ${
                                            form.interests.includes(it)
                                                ? 'bg-[#FFD500] text-black border-[#FFD500]'
                                                : 'bg-transparent text-white/75 border-white/15 hover:border-white/40'
                                        }`}
                                    >
                                        {it}
                                    </button>
                                ))}
                            </div>
                        </Field>

                        <Field label="Message *">
                            <textarea
                                data-testid={LANDING.contactMessage}
                                required
                                rows={5}
                                value={form.message}
                                onChange={onChange('message')}
                                placeholder="Décrivez votre besoin : quantités, secteur, délais…"
                                className="form-input resize-none"
                            />
                        </Field>

                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                            <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">
                                Réponse sous 24h ouvrées · Sans engagement
                            </p>
                            <button
                                data-testid={LANDING.contactSubmit}
                                type="submit"
                                disabled={submitting}
                                className="group inline-flex items-center justify-center gap-3 px-6 py-3.5 bg-[#FFD500] text-black font-bold uppercase tracking-wider text-sm hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all corner-cut"
                            >
                                {submitting ? (
                                    <>
                                        <span className="h-4 w-4 border-2 border-black border-r-transparent rounded-full animate-spin" />
                                        Envoi…
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-4 w-4" />
                                        Envoyer la demande
                                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                    </>
                                )}
                            </button>
                        </div>

                        <p className="text-[11px] leading-relaxed text-white/45">
                            En envoyant ce formulaire, vous acceptez que les informations transmises soient
                            utilisées exclusivement pour traiter votre demande. Aucune donnée n'est cédée à
                            des tiers. Consultez notre{' '}
                            <a href="/politique-de-confidentialite" className="underline text-[#FFD500]/80 hover:text-[#FFD500]">politique de confidentialité</a>{' '}
                            pour exercer vos droits (accès, rectification, suppression).
                        </p>

                        {status === 'success' && (
                            <div className="flex items-start gap-3 p-4 border border-[#FFD500]/40 bg-[#FFD500]/5 text-white/90 text-sm">
                                <CheckCircle2 className="h-5 w-5 text-[#FFD500] shrink-0 mt-0.5" />
                                <p>
                                    Merci ! Votre demande est bien enregistrée. Nous revenons vers vous très rapidement —
                                    en cas d'urgence, appelez le <strong>{BUSINESS.phone}</strong>.
                                </p>
                            </div>
                        )}
                        {status === 'error' && (
                            <div className="flex items-start gap-3 p-4 border border-red-500/40 bg-red-500/5 text-white/90 text-sm">
                                <AlertCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                                <p>
                                    Une erreur est survenue. Vous pouvez aussi nous joindre directement au{' '}
                                    <a href={`tel:${BUSINESS.phoneTel}`} className="underline">{BUSINESS.phone}</a>.
                                </p>
                            </div>
                        )}
                    </form>

                    {/* Info & map */}
                    <div className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <InfoCard icon={Phone} title="Appel direct" lines={[<a key="p" href={`tel:${BUSINESS.phoneTel}`} className="hover:text-[#FFD500] transition-colors">{BUSINESS.phone}</a>]} />
                            <InfoCard icon={Mail} title="Email" lines={[<a key="m" href={`mailto:${BUSINESS.email}`} className="hover:text-[#FFD500] transition-colors break-all">{BUSINESS.email}</a>]} />
                        </div>

                        <InfoCard
                            icon={MapPin}
                            title="Boutique MANU VET"
                            lines={[BUSINESS.address, `${BUSINESS.postalCode} ${BUSINESS.city}`, BUSINESS.department]}
                            cta={{ href: BUSINESS.googleMapsUrl, label: 'Itinéraire Google Maps' }}
                        />

                        <div className="corner-cut bg-[#0F0F0F] border border-white/10 p-5 sm:p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="inline-flex h-9 w-9 items-center justify-center bg-[#FFD500]/10 border border-[#FFD500]/30">
                                    <Clock className="h-4 w-4 text-[#FFD500]" />
                                </span>
                                <h3 className="font-display uppercase text-xl text-white">Horaires d'ouverture</h3>
                            </div>
                            <ul className="space-y-2.5">
                                {BUSINESS.openingHours.map((h) => (
                                    <li key={h.day} className="flex justify-between gap-4 text-sm border-b border-white/5 pb-2 last:border-0">
                                        <span className="text-white/75 uppercase tracking-wider text-xs font-semibold">{h.day}</span>
                                        <span className="text-white font-medium">{h.hours}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div
                            data-testid={LANDING.contactMap}
                            className="relative corner-cut overflow-hidden border border-white/10 aspect-[5/3]"
                        >
                            <iframe
                                title="Plan d'accès MANU VET Aulnay-sous-Bois"
                                src={BUSINESS.googleMapsEmbed}
                                width="100%"
                                height="100%"
                                style={{ border: 0, filter: 'grayscale(35%) contrast(105%)' }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .form-input {
                    width: 100%;
                    background-color: #0a0a0a;
                    border: 1px solid rgba(255,255,255,0.12);
                    color: #fff;
                    padding: 0.85rem 1rem;
                    font-size: 0.9rem;
                    font-family: 'Manrope', sans-serif;
                    outline: none;
                    transition: border-color 0.2s, box-shadow 0.2s;
                    border-radius: 2px;
                }
                .form-input:focus {
                    border-color: #FFD500;
                    box-shadow: 0 0 0 2px rgba(255, 213, 0, 0.15);
                }
                .form-input::placeholder {
                    color: rgba(255,255,255,0.3);
                }
            `}</style>
        </section>
    );
};

const Field = ({ label, children }) => (
    <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.22em] text-white/65 font-semibold mb-2">
            {label}
        </span>
        {children}
    </label>
);

const InfoCard = ({ icon: Icon, title, lines, cta }) => (
    <div className="corner-cut bg-[#0F0F0F] border border-white/10 p-5 sm:p-6 hover:border-[#FFD500]/40 transition-colors">
        <div className="flex items-center gap-3 mb-3">
            <span className="inline-flex h-9 w-9 items-center justify-center bg-[#FFD500]/10 border border-[#FFD500]/30">
                <Icon className="h-4 w-4 text-[#FFD500]" />
            </span>
            <h3 className="font-display uppercase text-lg text-white">{title}</h3>
        </div>
        <div className="space-y-1 text-sm text-white leading-relaxed">
            {lines.map((l, i) => (
                <div key={i}>{l}</div>
            ))}
        </div>
        {cta && (
            <a
                href={cta.href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#FFD500] hover:text-white transition-colors"
            >
                {cta.label}
                <ArrowRight className="h-3 w-3" />
            </a>
        )}
    </div>
);
